import env from '@/config/env';
import { CoreEnums } from '@/utils/domain/constants';
import mimeTypesGlossary from './mimeTypes';

const pdfjsLib = require('pdfjs-dist');
pdfjsLib.GlobalWorkerOptions.workerSrc = env.PDF_JS_WORKER_URL; //'//unpkg.com/pdfjs-dist@3.7.107/build/pdf.worker.min.js';

/**
 * Generate thumbnail of a PDF file
 * @param {String} filePath Document URL
 * @param {String} renderId Element ID to set the generated image
 */
export async function makePdfThumb(filePath) {
  try {
    const loadingTask = await pdfjsLib.getDocument(filePath).promise;
    const page = await loadingTask.getPage(1);

    const scale = 1;
    const viewport = page.getViewport({ scale });

    // Prepare canvas using PDF page dimensions
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // Render PDF page into canvas context
    await page.render({ canvasContext: context, viewport: viewport }).promise;

    return canvas.toDataURL();
  } catch (e) {}
}

/**
 * File size representation
 * @param {Number} number File size in bytes
 * @returns {string}
 */
export function fileSizeDisplay(n) {
  const size = 1024.0;

  if (n < 1024) return `${n} bytes`;
  if (n < size * size) return `${Math.round(n / size, 2)} Kb`;
  if (n < size * size * size) return `${Math.round(n / size / size, 2)} Mb`;

  return `${Math.round(n / size / size / size, 2)} Gb`;
}

/**
 * Convert a file to a Base64 string
 *
 * @param {File} file The file to convert
 * @param {Boolean} truncate: True: omit the content-type
 * @returns {Promise<string>} A promise that resolves to the Base64 string of the file
 */
export async function convertToBase64(file, truncate = true) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file.originFileObj ?? file);
    reader.onloadend = (ev) => {
      if (truncate) {
        resolve(ev.target.result.split(',').slice(-1)[0]);
      } else resolve(ev.target.result);
    };
  });
}

/**
 * Helper function to instantiate a new File blob
 *
 * @param {Blob} blob: File URL call response.blob()
 * @param {String} filename: File name
 * @returns {Promise<File>}
 */
export async function toOriginFileObj(blob, filename) {
  return new File([blob], filename, {
    type: mimeTypesGlossary[filename.split('.').slice(-1)[0]],
    lastModified: new Date().getTime(),
  });
}

/**
 * Helper function to get file base64 from source URL
 *
 * @param {String} url: File download URL
 * @param {String} filename: File name
 */
export async function toDataURL(url, filename = 'filename.jpg') {
  return fetch(url)
    .then((response) => response.blob())
    .then((blob) => toOriginFileObj(blob, filename));
}

/**
 * Convert base64 string to File object
 * @param {String} dataUrl: Base64 encoded string `data:[mimeType];base64,[base64 String]`
 * @param {String} filename: File name
 */
export function dataURLtoFile(dataUrl, filename = 'filename.jpg') {
  const arr = dataUrl.split(',');
  const mime = RegExp(/:(.*?);/).exec(arr[0])[1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export const fileContentTypeClassification = {
  'text/csv': CoreEnums.AttachmentFileTypeClassification.TextCsv,
  'text/plain': CoreEnums.AttachmentFileTypeClassification.Text,
  'text/html': CoreEnums.AttachmentFileTypeClassification.Html,
  'image/jpg': CoreEnums.AttachmentFileTypeClassification.Image,
  'image/jpeg': CoreEnums.AttachmentFileTypeClassification.Image,
  'image/gif': CoreEnums.AttachmentFileTypeClassification.Image,
  'image/bmp': CoreEnums.AttachmentFileTypeClassification.Image,
  'image/png': CoreEnums.AttachmentFileTypeClassification.Image,
  'video/mp4': CoreEnums.AttachmentFileTypeClassification.Video,
  'video/webm': CoreEnums.AttachmentFileTypeClassification.Video,
  'video/ogg': CoreEnums.AttachmentFileTypeClassification.Video,
  'video/3gpp': CoreEnums.AttachmentFileTypeClassification.Video,
  'video/x-msvideo': CoreEnums.AttachmentFileTypeClassification.Video,
  'application/pdf': CoreEnums.AttachmentFileTypeClassification.Pdf,
  'application/msword': CoreEnums.AttachmentFileTypeClassification.Word,
  'application/vnd.ms-excel': CoreEnums.AttachmentFileTypeClassification.Excel,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    CoreEnums.AttachmentFileTypeClassification.Excel,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    CoreEnums.AttachmentFileTypeClassification.Word,
  'application/zip': CoreEnums.AttachmentFileTypeClassification.Zip,
  'application/x-zip-compressed': CoreEnums.AttachmentFileTypeClassification.Zip,
  'application/x-zip': CoreEnums.AttachmentFileTypeClassification.Zip,
  'application/vnd.rar': CoreEnums.AttachmentFileTypeClassification.Rar,
  'application/x-rar-compressed': CoreEnums.AttachmentFileTypeClassification.Rar,
};

const extensionClassification = {
  zip: CoreEnums.AttachmentFileTypeClassification.Zip,
  rar: CoreEnums.AttachmentFileTypeClassification.Rar,
  html: CoreEnums.AttachmentFileTypeClassification.Html,
  htm: CoreEnums.AttachmentFileTypeClassification.Html,
};

export function resolveFileTypeClassification(contentType, fileName) {
  if (contentType && fileContentTypeClassification[contentType] != null) {
    return fileContentTypeClassification[contentType];
  }

  const ext =
    typeof fileName === 'string' && fileName.includes('.')
      ? fileName.split('.').pop()?.toLowerCase()
      : '';

  if (ext && extensionClassification[ext] != null) {
    return extensionClassification[ext];
  }

  return CoreEnums.AttachmentFileTypeClassification.Unknown;
}

/**
 * Percent-encode path segments that contain accents, spaces, or other unsafe chars.
 * Needed so Office Online / iframe / img can fetch blob URLs when the file name
 * has tildes (ó, ñ, …). Query string and hash are left intact; already-encoded
 * segments are not double-encoded.
 */
export function encodeFileUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  try {
    const parsed = new URL(url);
    parsed.pathname = parsed.pathname
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        try {
          return encodeURIComponent(decodeURIComponent(segment));
        } catch {
          return encodeURIComponent(segment);
        }
      })
      .join('/');
    return parsed.toString();
  } catch {
    return url.replace(/[^\u0000-\u007F]+/g, (ch) => encodeURIComponent(ch));
  }
}

export default {
  makePdfThumb,
  fileSizeDisplay,
  convertToBase64,
  toOriginFileObj,
  toDataURL,
  dataURLtoFile,
  fileContentTypeClassification,
  encodeFileUrl,
};
